import { useTranslation } from "react-i18next";
import { IOutletContext } from "../utils/interfaces/IOutletContext";
import { useOutletContext } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../utils/redux/userSlice";
import { Machine } from "../utils/models/Machine";
import { ISelectOptions } from "aw-combobox";
import { getAllMachines } from "../utils/services/machine.service";
import { useTheme } from "../utils/contexts/ThemeContext";
import { UserButtonsLine } from "aw-user-buttons-line";
import { DashboardTypes, MachineConected } from "aw-consts";
import { getTodayDate } from "aw-date-functions";
import ScatterChart, { DailyPoint, DailySeries } from "../components/ScatterChart/ScatterChart.component";
import { createColumnHelper } from "@tanstack/react-table";
import DataGrid from "../components/aw-data-grid/DataGrid";
import { ButtonGroup, Spinner } from "react-bootstrap";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { getLastXdays } from "aw-date-functions/dist/cjs/components/dateFunctions";
import { format } from "date-fns";

type Anomaly = {
    id: number,
    date: Date,
    type: string,
    score: number
}

const COLORS = [
    "#1890FF",
    "#F0D420",
    "#A66CFF",
    "#FF8C42"
];


const AnalitycModulePage = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const user = useSelector(selectUser)
    const context: IOutletContext = useOutletContext();
    const [machines, setMachines] = useState<Machine[]>([])
    const [machinesObj, setMachinesObj] = useState<ISelectOptions[]>([])
    const [selectedMachine, setSelectedMachine] = useState<ISelectOptions>({ value: 0, label: '' })
    const [loading, setLoading] = useState(true)
    const [iniDate, setIniDate] = useState(getLastXdays(30))
    const [finDate, setFinDate] = useState(getTodayDate())
    const [iniDateChart, setIniDateChart] = useState(getLastXdays(30))
    const [finDateChart, setFinDateChart] = useState(getTodayDate())
    const [anomalies, setAnomalies] = useState<Anomaly[]>([{ id: 1, date: new Date(), score: 0.2, type: '' }])
    const [series, setSeries] = useState<DailySeries[]>([]);
    const [rawPoints, setRawPoints] = useState<RawDailyPoint[]>([]);
    const [threshold, setThreshold] = useState<number>(0.25);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
    const [dataPie, setDataPie] = useState([
        { name: "heating_slope", value: 0 },
        { name: "time_to_setpoint", value: 0 },
        { name: "sensor_correlation_loss", value: 0 },
        { name: "temperature_variance", value: 0 }
    ]);

    useEffect(() => {
        context.setState(t('Analytics'));
    }, [context, t]);


    useEffect(() => {
        getAllMachines(user.token)
            .then((machines: Machine[]) => setMachines(machines))
            .catch((e) => console.log('Error get machines', e))
            .finally(() => setTimeout(() => setLoading(false), 1000))
    }, [])

    useEffect(() => {
        if (machines.length > 0) {
            const mObj: ISelectOptions[] = []
            machines.forEach((m) => {
                mObj.push({ value: m.idmachine, label: m.name })
            })
            mObj.sort((a, b) => Number(a.value) - Number(b.value))
            setMachinesObj(mObj)
            if (mObj[0])
                setSelectedMachine(mObj[0])
        } else
            setMachinesObj([])
    }, [machines])

    function getRandomAnomalyType(): string {
        const types = [
            "heating_slope",
            "time_to_setpoint",
            "sensor_correlation_loss",
            "temperature_variance"
        ];

        const index = Math.floor(Math.random() * types.length);
        return types[index];
    }

    useEffect(() => {
        const anomalySeries = series.find(s => s.id === "anomaly");
        if (anomalySeries) {
            const newAnomalies: Anomaly[] = anomalySeries.points.map((p, index) => ({
                id: index + 1,
                date: new Date(p.day),
                type: getRandomAnomalyType(),
                score: p.y
            }));
            setAnomalies(newAnomalies);
        }
    }, [series])

    useEffect(() => {
        const typeCounts: Record<string, number> = {
            "heating_slope": 0,
            "time_to_setpoint": 0,
            "sensor_correlation_loss": 0,
            "temperature_variance": 0
        };
        anomalies.forEach(a => {
            if (typeCounts[a.type] !== undefined) {
                typeCounts[a.type]++;
            }
        });
        const newDataPie = Object.keys(typeCounts).map(key => ({
            name: key,
            value: typeCounts[key]
        })).filter(entry => entry.value > 0);

        setDataPie(newDataPie);
    }, [anomalies])

    const onChangeDates = (e: any) => {
        if (e.target.name === "iniDate") {
            setIniDate(e.target.value)
        } else if (e.target.name === "finDate") {
            setFinDate(e.target.value)
        }
    }

    // hash 32-bit estable
    function hash32(str: string) {
        let h = 2166136261;
        for (let i = 0; i < str.length; i++) {
            h ^= str.charCodeAt(i);
            h = Math.imul(h, 16777619);
        }
        return h >>> 0;
    }

    // PRNG determinista (0..1)
    function mulberry32(seed: number) {
        return function () {
            let t = (seed += 0x6D2B79F5);
            t = Math.imul(t ^ (t >>> 15), t | 1);
            t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }

    type RawDailyPoint = {
        day: string; // YYYY-MM-DD
        y: number;
        meta?: Record<string, any>;
    };

    function buildRawPointsForRange(from: string, to: string, machineId: number): RawDailyPoint[] {
        const days: string[] = [];
        const start = new Date(from + "T00:00:00");
        const end = new Date(to + "T00:00:00");

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const yy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            days.push(`${yy}-${mm}-${dd}`);
        }

        const MIN = 0.05;
        const MAX = 0.45;
        const clamp = (v: number) => Math.max(MIN, Math.min(MAX, v));

        const rngBase = mulberry32(hash32(Math.random().toString()));

        let prev = clamp(0.05 + rngBase() * 0.30);

        const points: RawDailyPoint[] = [];

        for (let i = 0; i < days.length; i++) {
            const day = days[i];

            // PRNG per aquest dia (determinista)
            const rng = mulberry32(hash32(Math.random().toString()));
            const r = rng();   // 0..1
            const r2 = rng();  // 0..1

            // random-walk suau
            const step = (r - 0.5) * 0.60;
            let y = prev + step;

            // suavitzat
            y = prev * 0.30 + y * 0.5;

            y = clamp(y);
            prev = y;

            points.push({
                day,
                y: Number(y.toFixed(2)),
                meta: { runId: `ID-${Math.floor(rng() * 900 + 100)}` },
            });
        }

        return points;
    }

    function splitByThreshold(points: RawDailyPoint[], threshold: number): DailySeries[] {
        const normalPoints: DailyPoint[] = [];
        const anomalyPoints: DailyPoint[] = [];

        points.forEach((p) => {
            const meta = {
                ...(p.meta ?? {}),
                threshold,
                severity:
                    p.y >= 0.72 ? "High" :
                        p.y >= 0.60 ? "Medium" :
                            p.y >= threshold ? "Low" : "Normal",
            };

            const out: DailyPoint = { day: p.day, y: p.y, meta };

            if (p.y >= threshold) anomalyPoints.push(out);
            else normalPoints.push(out);
        });

        return [
            { id: "normal", label: t("Normal"), color: "#00c389", points: normalPoints, pointRadius: 4 },
            { id: "anomaly", label: t("Anomaly"), color: "#ff4d4f", points: anomalyPoints, pointRadius: 4 },
        ];
    }

    useMemo(() => {
        if (loading)
            setRawPoints(buildRawPointsForRange(iniDate, finDate, Number(selectedMachine.value)));
    }, [loading, selectedMachine.value]);

    useMemo(() => {
        const newSeries = splitByThreshold(rawPoints, threshold);
        setSeries(newSeries);
    }, [rawPoints, threshold]);

    const columnHelper = createColumnHelper<Anomaly>();

    const columns = [
        columnHelper.accessor("id", {
            header: t("ID"),
            enableSorting: true,
            enableColumnFilter: false,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor("date", {
            header: t("Date"),
            enableSorting: true,

            cell: (info) => format(new Date(info.getValue()), "dd/MM/yyyy"),
            enableColumnFilter: false
        }),
        columnHelper.accessor("type", {
            header: t("Type"),
            enableSorting: true,

            cell: info => info.getValue(),
        }),
        columnHelper.accessor("score", {
            header: t("Score"),
            enableSorting: true,

            cell: info => info.getValue(),
        }),
        columnHelper.display({
            id: "actions",
            header: "",
            enableSorting: false,
            enableColumnFilter: false,
            cell: ({ row }) => {
                return (
                    <div className="text-end">
                        <ButtonGroup>
                            <button
                                onClick={(e) => { e.stopPropagation(); }}
                                className="btn-Simple"
                            >
                                <i className="bi bi-check2" style={{ color: 'var(--accent-color)', fontSize: '1.2rem' }} />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); }}
                                className="btn-Simple"
                            >
                                <i className="bi bi-x text-danger" style={{ fontSize: '1.2rem' }} />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); }}
                                className="btn-Simple"
                            >
                                <i className="bi bi-info text-primary" style={{ fontSize: '1.2rem' }} />
                            </button>
                        </ButtonGroup>
                    </div>
                );
            },
        }),
    ];

    const onClickReload = () => {
        setIniDateChart(iniDate);
        setFinDateChart(finDate);
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
        }, 1000);
    }

    const onChangeSelect = (e: ISelectOptions) => {
        setIniDateChart(iniDate);
        setFinDateChart(finDate);
        setSelectedMachine(e);
        onClickReload()
    }

    return (
        <>
            <div className='row border-0 mx-2' style={{ maxWidth: '100%' }}>
                <div className='col-12 p-1'>
                    <UserButtonsLine downloardData={() => null} role={user.role} connected={MachineConected.Conected} defaultIni={iniDate}
                        defaultFin={finDate} onChangeDates={onChangeDates} onChangeSelect={onChangeSelect} onClickReload={onClickReload} options={machinesObj}
                        selected={selectedMachine} DashboardType={DashboardTypes.MachineGroup} changeView={() => null} rounded='rounded-1' classNameSecondary='btn-Custom btn-IDGrey'
                        classNameSuccess='btn-Custom btn-IDGreen' containerColor='var(--background-secondary)' dark={theme.theme === 'dark' ? true : false} downloadButton={false} />
                </div>
            </div >
            <div className='row border-0 mx-2' style={{ maxWidth: '100%' }}>
                <div className='col-12 col-md ps-1 pe-1 mt-1'>
                    <div className="p-2 rounded-1" style={{ height: '100%', backgroundColor: 'var(--background-secondary)', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }}>
                        <div className="text-center d-flex flex-row align-items-center ">
                            <span className="h3 w-100">{t("Dispersion")}</span>
                            <input type="number" max={0.5} min={0} step={0.01} value={threshold} style={{ width: '5rem' }} onChange={(e) => setThreshold(Number(e.target.value))} className="form-control mx-auto mt-2" />
                        </div>
                        {!loading ? <div className="p-2"><ScatterChart
                            from={iniDateChart}
                            to={finDateChart}
                            yMin={0}
                            yMax={0.5}
                            thresholdEnabled
                            thresholdValue={threshold}
                            onThresholdChange={(v) => setThreshold(v)}
                            series={series}
                        /></div>
                            : <div className="d-flex justify-content-center align-items-center" style={{ height: '25rem' }}>
                                <Spinner animation="border" role="status" variant="success" />
                            </div>}

                    </div>
                </div>
            </div >
            <div className='row border-0 mx-2 mb-2' style={{ maxWidth: '100%' }}>
                <div className='col-12 col-md-6 ps-1 pe-1 mt-2'>
                    <div className="p-2 rounded-1" style={{ height: '30rem', overflow: 'hidden', overflowY: 'auto', backgroundColor: 'var(--background-secondary)', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }}>
                        <div className="text-center">
                            <span className="h3">{t("DetectedAnomalies")}</span>
                        </div>
                        {!loading ? anomalies.length > 0 ? <DataGrid
                            data={anomalies}
                            paginationEnabled={true}
                            pagination={pagination}
                            onPaginationChange={(e) => setPagination(e)}
                            columns={columns}
                        /> : <div className="text-center mt-3">{t("NoAnomalies")}</div> : <div className="d-flex justify-content-center align-items-center" style={{ height: '25rem' }}>
                            <Spinner animation="border" role="status" variant="success" />
                        </div>}
                    </div>
                </div>
                <div className='col-12 col-md-6 ps-1 pe-1 mt-2'>
                    <section className="rounded-1 text-center pt-2" style={{ backgroundColor: 'var(--background-secondary)', height: '30rem', color: 'var(--text-primary)', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }}>
                        <div className="text-center">
                            <span className="h3">{t("DistributionOfAnomalies")}</span>
                        </div>
                        {!loading ? dataPie.length > 0 ? <ResponsiveContainer width="100%" height="80%">
                            <PieChart>
                                <Pie
                                    dataKey="value"
                                    isAnimationActive={true}
                                    data={dataPie}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={'75%'}
                                    label={({ cx, cy, midAngle, innerRadius, outerRadius, value, name }) => {
                                        const RADIAN = Math.PI / 180;
                                        const radius = 25 + outerRadius;
                                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                        const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                        return (
                                            <text
                                                x={x}
                                                y={y}
                                                fill="var(--text-primary)"
                                                textAnchor={x > cx ? "start" : "end"}
                                                dominantBaseline="central"
                                            >
                                                {`${name}`}
                                            </text>
                                        );
                                    }}

                                >
                                    {dataPie.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer> : <div className="text-center mt-3">{t("NoAnomalies")}</div> :
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '25rem' }}>
                                <Spinner animation="border" role="status" variant="success" />
                            </div>}
                    </section>
                </div>
            </div >

        </>
    )
}

export default AnalitycModulePage;
